'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { MessageWithSender } from '../types';

// Monotonic, process-wide counter for realtime channel names. Date.now() alone
// can collide within a millisecond under Strict Mode double-invoke, which makes
// Supabase return an already-subscribing channel and throws "cannot add
// postgres_changes callbacks ... after subscribe()" (see Sentry 7511365018 for
// the sibling chat-messages channel). The counter guarantees a fresh channel.
let realtimeChannelSeq = 0;

interface UseChatRealtimeOptions {
  conversationId: string | null;
  onNewMessage?: (message: MessageWithSender) => void;
  onMessageUpdated?: (message: MessageWithSender) => void;
  onMessageDeleted?: (messageId: string) => void;
  onReadReceiptUpdated?: (userId: string, lastReadAt: string) => void;
  onTypingChange?: (userId: string, isTyping: boolean) => void;
}

/**
 * Hook to manage chat realtime subscriptions for a conversation
 *
 * This hook provides:
 * - Real-time message delivery
 * - Read receipt updates
 * - Typing indicators (future)
 */
export function useChatRealtime(options: UseChatRealtimeOptions) {
  const {
    conversationId,
    onNewMessage,
    onMessageUpdated,
    onMessageDeleted,
    onReadReceiptUpdated,
    onTypingChange,
  } = options;

  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [activeTools, setActiveTools] = useState<string[]>([]);

  // Use refs for callbacks to avoid re-subscribing on every callback change
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageUpdatedRef = useRef(onMessageUpdated);
  const onMessageDeletedRef = useRef(onMessageDeleted);
  const onReadReceiptUpdatedRef = useRef(onReadReceiptUpdated);
  const onTypingChangeRef = useRef(onTypingChange);

  // Keep refs updated (in effect to satisfy React compiler rules)
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
    onMessageUpdatedRef.current = onMessageUpdated;
    onMessageDeletedRef.current = onMessageDeleted;
    onReadReceiptUpdatedRef.current = onReadReceiptUpdated;
    onTypingChangeRef.current = onTypingChange;
  });

  // Set up realtime subscription
  useEffect(() => {
    if (!conversationId) {
      return;
    }

    // Clean up existing subscription
    if (channelRef.current) {
      client.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset error on new subscription setup
    setConnectionError(null);

    // Use a unique channel name per subscription instance to prevent Supabase
    // from returning an already-subscribed channel when the effect re-runs
    // (React Strict Mode double-invoke or conversationId change). Adding a
    // timestamp suffix guarantees a fresh channel is created so all .on()
    // listeners are registered before .subscribe() is called.
    // Without this, Supabase throws "cannot add postgres_changes callbacks
    // after subscribe()" (Sentry BALLEE-DD).
    const channel = client
      .channel(
        `conversation:${conversationId}:${Date.now()}:${++realtimeChannelSeq}`,
      )
      // Subscribe to new messages
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const message = payload.new as MessageWithSender;

          // Add sender placeholder. BAL-580: an organization message has no
          // sender id in the realtime row; its org and author arrive with the
          // refetch below.
          if (!message.sender && message.sender_id) {
            message.sender = {
              id: message.sender_id,
              first_name: null,
              last_name: null,
              profile_photo_url: null,
            };
          }

          onNewMessageRef.current?.(message);

          // Invalidate queries to refetch with full data
          queryClient.invalidateQueries({
            queryKey: ['chat-messages', conversationId],
          });
          queryClient.invalidateQueries({
            queryKey: ['chat-conversations'],
          });
          queryClient.invalidateQueries({
            queryKey: ['chat-total-unread-count'],
          });
        },
      )
      // Subscribe to message updates (edits)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const message = payload.new as MessageWithSender;

          // Check if it's a delete (soft delete)
          if (message.is_deleted) {
            onMessageDeletedRef.current?.(message.id);
          } else {
            onMessageUpdatedRef.current?.(message);
          }

          queryClient.invalidateQueries({
            queryKey: ['chat-messages', conversationId],
          });
        },
      )
      // Subscribe to read receipt updates
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversation_participants',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const participant = payload.new as {
            user_id: string;
            last_read_at: string;
          };

          onReadReceiptUpdatedRef.current?.(
            participant.user_id,
            participant.last_read_at,
          );

          // Update conversation cache with new read receipt
          queryClient.invalidateQueries({
            queryKey: ['chat-conversation', conversationId],
          });
        },
      )
      // Subscribe to reaction changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
        },
        (payload) => {
          const reaction =
            (payload.new as { message_id?: string }) ||
            (payload.old as { message_id?: string });
          if (reaction?.message_id) {
            queryClient.invalidateQueries({
              queryKey: ['chat-reactions', reaction.message_id],
            });
          }
        },
      )
      // Subscribe to typing indicator broadcasts
      .on('broadcast', { event: 'typing' }, (payload) => {
        const data = payload.payload as {
          user_id?: string;
          is_typing?: boolean;
        };
        if (data?.user_id) {
          setTypingUsers((prev) => {
            const next = new Set(prev);
            if (data.is_typing) {
              next.add(data.user_id!);
            } else {
              next.delete(data.user_id!);
              // Clear tool progress when typing stops (response complete)
              setActiveTools([]);
            }
            return next;
          });
          onTypingChangeRef.current?.(data.user_id, !!data.is_typing);
        }
      })
      // Subscribe to tool execution progress broadcasts
      .on('broadcast', { event: 'tool_progress' }, (payload) => {
        const data = payload.payload as {
          tool_name?: string;
          status?: 'running' | 'completed';
        };
        if (data?.tool_name) {
          setActiveTools((prev) => {
            // Add new tool to the list (keep last 3 for display)
            const next = [...prev, data.tool_name!];
            return next.slice(-3);
          });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          setConnectionError(null);
        } else if (status === 'CHANNEL_ERROR') {
          setIsConnected(false);
          setConnectionError(new Error('Failed to connect to chat channel'));
        } else if (status === 'TIMED_OUT') {
          setIsConnected(false);
          setConnectionError(new Error('Chat channel connection timed out'));
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
        setIsConnected(false);
      }
    };
  }, [client, conversationId, queryClient]); // Removed callback dependencies - using refs instead

  // Reconnect handler
  const reconnect = useCallback(() => {
    if (!conversationId) return;

    // Remove and recreate the subscription
    if (channelRef.current) {
      client.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // The effect will recreate the subscription
    setConnectionError(null);
  }, [client, conversationId]);

  return {
    /**
     * Whether the current client is connected to the Realtime channel.
     * NOTE: This is the channel connection status, NOT user online presence.
     * To check if another user is online, use useIsUserOnline() from use-presence.ts
     */
    isConnected,
    connectionError,
    reconnect,
    /** Set of user IDs currently typing in this conversation */
    typingUsers,
    /** Recently executed tool names (last 3) — for showing AI progress */
    activeTools,
  };
}

/**
 * Hook to manage global chat notifications (new messages in any conversation)
 */
export function useGlobalChatNotifications(
  options: {
    onNewMessage?: (conversationId: string, message: MessageWithSender) => void;
  } = {},
) {
  const { onNewMessage } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const monitoring = useMonitoring();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Use ref for callback to avoid re-subscribing
  const onNewMessageRef = useRef(onNewMessage);
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  });

  useEffect(() => {
    let isMounted = true;

    const setupSubscription = async () => {
      try {
        const {
          data: { user },
        } = await client.auth.getUser();

        // Check if component is still mounted
        if (!isMounted || !user) return;

        // Clean up existing subscription
        if (channelRef.current) {
          client.removeChannel(channelRef.current);
        }

        // Subscribe to all conversations the user participates in
        const channel = client
          .channel('global-chat-notifications')
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'conversations',
            },
            () => {
              // Invalidate conversations list
              queryClient.invalidateQueries({
                queryKey: ['chat-conversations'],
              });
              queryClient.invalidateQueries({
                queryKey: ['chat-total-unread-count'],
              });
            },
          )
          .subscribe((status) => {
            if (isMounted) {
              setIsConnected(status === 'SUBSCRIBED');
            }
          });

        channelRef.current = channel;
      } catch (error) {
        // Capture but don't crash - subscription is non-critical
        void monitoring.captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };

    setupSubscription();

    return () => {
      isMounted = false;
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
        setIsConnected(false);
      }
    };
  }, [client, queryClient, monitoring]); // Removed onNewMessage - using ref instead

  return { isConnected };
}
